import { Injectable } from '@angular/core';
import { Observable, delay, of, throwError } from 'rxjs';
import type { FinancialSnapshotApi } from '../financial-snapshot-api.token';
import type {
  FinancialSnapshotDocument,
  FinancialSnapshotLoadResult,
  FinancialSnapshotPatchBody,
  FinancialSnapshotPostBody,
  FinancialSnapshotResponse,
  FinancialSnapshotTemplate,
  SnapshotStatus,
} from '../financial-snapshot.model';
import { recomputeSnapshot } from './financial-snapshot-compute';
import {
  COMPLETE_SNAPSHOT_FIXTURE,
  IN_PROGRESS_SNAPSHOT_FIXTURE,
  OUTDATED_SNAPSHOT_FIXTURE,
} from './snapshot.fixtures';
import { FINANCIAL_SNAPSHOT_TEMPLATE_FIXTURE } from './template.fixture';

export type MockSnapshotSeed = 'empty' | SnapshotStatus;

function cloneDeep<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

@Injectable()
export class MockFinancialSnapshotApiService implements FinancialSnapshotApi {
  /** Simulated latency (ms). High enough in mock to exercise loading skeletons in the UI. */
  latencyMs = 2000;

  /** When set, next getSnapshot fails once then clears. */
  nextGetError: Error | null = null;

  private readonly _store = new Map<string, FinancialSnapshotDocument>();
  private _template: FinancialSnapshotTemplate = cloneDeep(FINANCIAL_SNAPSHOT_TEMPLATE_FIXTURE);
  private _devForceStatus: MockSnapshotSeed | null = null;

  getTemplate(): Observable<FinancialSnapshotTemplate> {
    // Template is small/cacheable — keep snappy so skeleton time is driven by getSnapshot.
    return of(cloneDeep(this._template));
  }

  getSnapshot(guid: string): Observable<FinancialSnapshotLoadResult> {
    if (this.nextGetError) {
      const err = this.nextGetError;
      this.nextGetError = null;
      return throwError(() => err).pipe(delay(this.latencyMs));
    }

    if (this._devForceStatus === 'empty') {
      return of({ kind: 'empty' as const }).pipe(delay(this.latencyMs));
    }

    const doc = this._store.get(guid);
    if (!doc) {
      return of({ kind: 'empty' as const }).pipe(delay(this.latencyMs));
    }

    let forCompute = doc;
    if (this._devForceStatus === 'in_progress') {
      forCompute = { ...doc, submittedDate: undefined };
    } else if (this._devForceStatus === 'complete') {
      forCompute = {
        ...doc,
        submittedDate: doc.submittedDate ?? new Date().toISOString(),
      };
    } else if (this._devForceStatus === 'outdated') {
      forCompute = {
        ...doc,
        submittedDate: '2025-01-01T10:00:00.000Z',
      };
    }

    const snapshot = recomputeSnapshot(forCompute, this._template);
    return of({ kind: 'snapshot' as const, snapshot }).pipe(delay(this.latencyMs));
  }

  postSnapshot(body: FinancialSnapshotPostBody): Observable<FinancialSnapshotResponse> {
    const now = new Date().toISOString();
    const existing = this._store.get(body.guid);
    const doc: FinancialSnapshotDocument = {
      guid: body.guid,
      createdDate: body.createdDate ?? existing?.createdDate ?? now,
      modifiedDate: now,
      lastUpdatedAt: now,
      submittedDate: body.submittedDate ?? existing?.submittedDate,
      categories: cloneDeep(body.categories),
    };
    this._store.set(body.guid, doc);
    return of(recomputeSnapshot(doc, this._template)).pipe(delay(this.latencyMs));
  }

  patchCategory(guid: string, body: FinancialSnapshotPatchBody): Observable<FinancialSnapshotResponse> {
    const existing = this._store.get(guid);
    if (!existing) {
      return throwError(() => new Error('Snapshot not found')).pipe(delay(this.latencyMs));
    }
    const now = new Date().toISOString();
    const doc: FinancialSnapshotDocument = {
      ...existing,
      modifiedDate: now,
      lastUpdatedAt: now,
      categories: {
        ...existing.categories,
        ...cloneDeep(body),
      },
    };
    this._store.set(guid, doc);
    return of(recomputeSnapshot(doc, this._template)).pipe(delay(this.latencyMs));
  }

  /** Dev/test: seed or clear a guid from fixtures. */
  seed(guid: string, seed: MockSnapshotSeed): void {
    this._devForceStatus = null;
    if (seed === 'empty') {
      this._store.delete(guid);
      return;
    }
    const base =
      seed === 'in_progress'
        ? IN_PROGRESS_SNAPSHOT_FIXTURE
        : seed === 'outdated'
          ? OUTDATED_SNAPSHOT_FIXTURE
          : COMPLETE_SNAPSHOT_FIXTURE;
    this._store.set(guid, cloneDeep({ ...base, guid }));
  }

  /** Dev-only: force GET to report a status without mutating store. */
  setDevForceStatus(status: MockSnapshotSeed | null): void {
    this._devForceStatus = status;
  }

  clear(): void {
    this._store.clear();
    this._devForceStatus = null;
    this.nextGetError = null;
  }
}
